import type { MagneticWallFocus } from '../../editor/editorSlice';
import type {
  MagneticWallContentRect,
  MagneticWallDisplayItem,
} from '../MagneticWall';
import { MINIMIZED_COMMENT_HEIGHT } from './CommentThreadMinimized';

const POPOVER_BELOW_CONTENT_OFFSET = 8;
const AVOID_CONTENT_THRESHOLD_HEIGHT = 380;
export const THREAD_SPACING = 12;

export type Position = { top: number; isMinimized: boolean };

const getPositionsWithoutOverlaps = ({
  heights,
  magneticWallItems,
  magneticWallFocus,
  showingCompactContainer,
  widerLineRanges,
  contentRects,
}: {
  heights: Record<string, number | null>;
  magneticWallItems: MagneticWallDisplayItem[];
  magneticWallFocus: MagneticWallFocus | null;
  showingCompactContainer: boolean;
  widerLineRanges: { top: number; bottom: number }[];
  contentRects: Record<string, MagneticWallContentRect>;
}): Record<string, Position> => {
  const filteredItems = magneticWallItems.filter(
    ({ data }) => contentRects[data.id],
  );

  if (!filteredItems.length) {
    return {};
  }

  const sorted = filteredItems.sort((a, b) => {
    return contentRects[a.data.id]?.top === contentRects[b.data.id]?.top
      ? (contentRects[a.data.id]?.left ?? 0) -
          (contentRects[b.data.id]?.left ?? 0)
      : (contentRects[a.data.id]?.top ?? 0) -
          (contentRects[b.data.id]?.top ?? 0);
  });

  const getTop = (item: MagneticWallDisplayItem) => {
    const contentRect = contentRects[item.data.id];

    const avoidContentOffset =
      item.data.id === magneticWallFocus?.id &&
      magneticWallFocus.active &&
      showingCompactContainer &&
      contentRect &&
      contentRect.bottom - contentRect.top < AVOID_CONTENT_THRESHOLD_HEIGHT
        ? contentRect.bottom - contentRect.top + POPOVER_BELOW_CONTENT_OFFSET
        : 0;

    return contentRect ? contentRect.top + avoidContentOffset : 0;
  };

  const priorityIndex = Math.max(
    0,
    sorted.findIndex(
      (item) =>
        magneticWallFocus &&
        item.data.id === magneticWallFocus.id &&
        item.type === magneticWallFocus.type,
    ),
  );

  const priority = sorted[priorityIndex];

  const getHeight = (item: MagneticWallDisplayItem) =>
    heights[item.displayId] || MINIMIZED_COMMENT_HEIGHT;

  const besideWiderLines = (top: number, height: number) =>
    widerLineRanges.some(
      (range) => top + height >= range.top && top <= range.bottom,
    );

  const adjustedPositions = priority
    ? {
        [priority.displayId]: {
          top: getTop(priority),
          isMinimized: besideWiderLines(
            getTop(priority),
            magneticWallFocus ? MINIMIZED_COMMENT_HEIGHT : getHeight(priority),
          ),
        },
      }
    : {};

  for (let i = priorityIndex - 1; i >= 0; i -= 1) {
    const current = sorted[i];
    const next = sorted[i + 1];
    const nextAdjustedPosition = next
      ? adjustedPositions[next.displayId]
      : null;

    if (current && nextAdjustedPosition) {
      const expectedTopOffset = getTop(current);
      const adjustedTopOffset =
        nextAdjustedPosition.top - getHeight(current) - THREAD_SPACING;

      const upperTopOffset = Math.min(expectedTopOffset, adjustedTopOffset);
      const isMinimized = besideWiderLines(upperTopOffset, getHeight(current));

      adjustedPositions[current.displayId] = {
        isMinimized,
        top:
          upperTopOffset === adjustedTopOffset && isMinimized
            ? upperTopOffset +
              Math.max(getHeight(current) - MINIMIZED_COMMENT_HEIGHT, 0)
            : upperTopOffset,
      };
    }
  }

  for (let i = priorityIndex + 1; i < sorted.length; i += 1) {
    const current = sorted[i];
    const prev = sorted[i - 1];

    const adjustedPrevPosition = prev
      ? adjustedPositions[prev.displayId]
      : null;

    if (current && prev && adjustedPrevPosition) {
      const expectedTopOffset = getTop(current);
      const adjustedTopOffset =
        adjustedPrevPosition.top +
        (adjustedPrevPosition.isMinimized
          ? MINIMIZED_COMMENT_HEIGHT
          : getHeight(prev)) +
        THREAD_SPACING;

      const lowerTopOffset = Math.max(expectedTopOffset, adjustedTopOffset);
      const isMinimized = besideWiderLines(lowerTopOffset, getHeight(current));

      adjustedPositions[current.displayId] = {
        isMinimized,
        top: lowerTopOffset,
      };
    }
  }

  return adjustedPositions;
};

export default getPositionsWithoutOverlaps;
